import React, { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Calendar, Ruler, Image, X, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';

const WoundHealingTracker = ({ woundImages = [], woundMeasurements = [] }) => {
  const [selectedView, setSelectedView] = useState('timeline');
  const [compareImages, setCompareImages] = useState({ before: null, after: null });
  const [showComparison, setShowComparison] = useState(false);

  // Sort images by date
  const sortedImages = useMemo(() => {
    return [...(woundImages || [])].sort((a, b) => new Date(a.dateTaken) - new Date(b.dateTaken));
  }, [woundImages]);

  // Calculate healing metrics
  const healingMetrics = useMemo(() => {
    if (sortedImages.length < 2) return null;

    const firstImage = sortedImages[0];
    const lastImage = sortedImages[sortedImages.length - 1];
    
    // Calculate days between first and last image
    const daysBetween = Math.ceil(
      (new Date(lastImage.dateTaken) - new Date(firstImage.dateTaken)) / (1000 * 60 * 60 * 24)
    );

    // If we have measurements, calculate area changes
    const measurements = woundMeasurements || [];
    if (measurements.length >= 2) {
      const firstMeasurement = measurements[0];
      const lastMeasurement = measurements[measurements.length - 1];
      
      const firstArea = (firstMeasurement.width || 0) * (firstMeasurement.length || 0);
      const lastArea = (lastMeasurement.width || 0) * (lastMeasurement.length || 0);
      
      const areaChange = firstArea > 0 ? ((firstArea - lastArea) / firstArea * 100).toFixed(1) : 0;
      const isHealing = lastArea < firstArea;

      return {
        daysBetween,
        firstArea: firstArea.toFixed(2),
        lastArea: lastArea.toFixed(2),
        areaChange: Math.abs(areaChange),
        isHealing,
        totalImages: sortedImages.length
      };
    }

    return {
      daysBetween,
      totalImages: sortedImages.length,
      firstArea: null,
      lastArea: null,
      areaChange: null,
      isHealing: null
    };
  }, [sortedImages, woundMeasurements]);

  // Handle comparison selection
  const handleSelectForComparison = (image, slot) => {
    setCompareImages(prev => ({ ...prev, [slot]: image }));
  };

  if (!woundImages || woundImages.length === 0) {
    return (
      <div className="border rounded-lg p-6 bg-gray-50 text-center">
        <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No wound images available for tracking</p>
        <p className="text-sm text-gray-400 mt-1">Upload wound images to see healing progress</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white shadow-sm">
      {/* Header with View Tabs */}
      <div className="border-b p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-lg">Wound Healing Progress</h3>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {['timeline', 'compare', 'metrics'].map((view) => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${
                selectedView === view 
                  ? 'bg-white shadow text-blue-600 font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`wound-tracker-${view}-tab`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {/* Healing Metrics Summary */}
        {healingMetrics && (
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <Calendar className="w-5 h-5 mx-auto text-blue-600 mb-1" />
              <p className="text-xs text-gray-500">Tracking Period</p>
              <p className="text-lg font-semibold text-blue-800">{healingMetrics.daysBetween} days</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <Image className="w-5 h-5 mx-auto text-purple-600 mb-1" />
              <p className="text-xs text-gray-500">Total Images</p>
              <p className="text-lg font-semibold text-purple-800">{healingMetrics.totalImages}</p>
            </div>
            {healingMetrics.areaChange !== null && (
              <>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Ruler className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                  <p className="text-xs text-gray-500">Initial Area</p>
                  <p className="text-lg font-semibold text-gray-800">{healingMetrics.firstArea} cm²</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${healingMetrics.isHealing ? 'bg-green-50' : 'bg-red-50'}`}>
                  {healingMetrics.isHealing ? (
                    <TrendingDown className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  ) : (
                    <TrendingUp className="w-5 h-5 mx-auto text-red-600 mb-1" />
                  )}
                  <p className="text-xs text-gray-500">Size Change</p>
                  <p className={`text-lg font-semibold ${healingMetrics.isHealing ? 'text-green-800' : 'text-red-800'}`}>
                    {healingMetrics.isHealing ? '↓' : '↑'} {healingMetrics.areaChange}%
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Timeline View */}
        {selectedView === 'timeline' && (
          <div className="relative" data-testid="wound-healing-timeline">
            {/* Timeline Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-yellow-500 to-red-400" />
            
            {/* Timeline Items */}
            <div className="space-y-4">
              {sortedImages.map((image, index) => (
                <div key={image.id} className="flex items-start gap-4 relative">
                  {/* Timeline Dot */}
                  <div className={`w-3 h-3 rounded-full z-10 mt-1.5 ${
                    index === 0 ? 'bg-green-500' : 
                    index === sortedImages.length - 1 ? 'bg-blue-500' : 'bg-yellow-500'
                  }`} />
                  
                  {/* Content */}
                  <div className="flex-1 bg-gray-50 rounded-lg p-3 flex gap-4">
                    <img 
                      src={image.imageData} 
                      alt={`Wound - ${image.dateTaken}`}
                      className="w-24 h-24 object-cover rounded-lg border-2 border-white shadow cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => {
                        setCompareImages({ before: sortedImages[0], after: image });
                        setShowComparison(true);
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          index === 0 ? 'bg-green-100 text-green-800' : 
                          index === sortedImages.length - 1 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {index === 0 ? 'Initial' : index === sortedImages.length - 1 ? 'Latest' : `Day ${Math.ceil((new Date(image.dateTaken) - new Date(sortedImages[0].dateTaken)) / (1000 * 60 * 60 * 24))}`}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(image.dateTaken).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </p>
                      {image.description && (
                        <p className="text-sm text-gray-600 mt-1">{image.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compare View */}
        {selectedView === 'compare' && (
          <div data-testid="wound-healing-compare">
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Before Selection */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Before (Select):</p>
                <div className="flex gap-2 flex-wrap mb-2">
                  {sortedImages.map((img, idx) => (
                    <button
                      key={img.id}
                      onClick={() => handleSelectForComparison(img, 'before')}
                      className={`w-12 h-12 rounded border-2 overflow-hidden transition-all ${
                        compareImages.before?.id === img.id ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img src={img.imageData} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                {compareImages.before ? (
                  <div className="border rounded-lg p-2 bg-green-50">
                    <img 
                      src={compareImages.before.imageData} 
                      alt="Before" 
                      className="w-full h-48 object-contain rounded"
                    />
                    <p className="text-center text-sm mt-2 font-medium text-green-800">
                      {new Date(compareImages.before.dateTaken).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400">
                    Select a &quot;Before&quot; image
                  </div>
                )}
              </div>

              {/* After Selection */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">After (Select):</p>
                <div className="flex gap-2 flex-wrap mb-2">
                  {sortedImages.map((img) => (
                    <button
                      key={img.id}
                      onClick={() => handleSelectForComparison(img, 'after')}
                      className={`w-12 h-12 rounded border-2 overflow-hidden transition-all ${
                        compareImages.after?.id === img.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img src={img.imageData} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                {compareImages.after ? (
                  <div className="border rounded-lg p-2 bg-blue-50">
                    <img 
                      src={compareImages.after.imageData} 
                      alt="After" 
                      className="w-full h-48 object-contain rounded"
                    />
                    <p className="text-center text-sm mt-2 font-medium text-blue-800">
                      {new Date(compareImages.after.dateTaken).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400">
                    Select an &quot;After&quot; image
                  </div>
                )}
              </div>
            </div>

            {/* Comparison Summary */}
            {compareImages.before && compareImages.after && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Comparing images from{' '}
                  <span className="font-medium text-green-700">{new Date(compareImages.before.dateTaken).toLocaleDateString()}</span>
                  {' '}to{' '}
                  <span className="font-medium text-blue-700">{new Date(compareImages.after.dateTaken).toLocaleDateString()}</span>
                </p>
                <p className="text-lg font-semibold text-gray-800 mt-1">
                  {Math.ceil((new Date(compareImages.after.dateTaken) - new Date(compareImages.before.dateTaken)) / (1000 * 60 * 60 * 24))} days apart
                </p>
              </div>
            )}
          </div>
        )}

        {/* Metrics View */}
        {selectedView === 'metrics' && (
          <div data-testid="wound-healing-metrics">
            {healingMetrics ? (
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Healing Progress</span>
                    <span className={`text-sm font-bold ${healingMetrics.isHealing ? 'text-green-600' : 'text-orange-600'}`}>
                      {healingMetrics.areaChange !== null ? `${healingMetrics.areaChange}% ${healingMetrics.isHealing ? 'reduced' : 'increased'}` : 'Measurements needed'}
                    </span>
                  </div>
                  {healingMetrics.areaChange !== null && (
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div 
                        className={`h-4 rounded-full transition-all duration-500 ${healingMetrics.isHealing ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-orange-400 to-red-500'}`}
                        style={{ width: `${Math.min(100, healingMetrics.areaChange)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-800">{sortedImages.length}</p>
                    <p className="text-sm text-gray-500">Total Assessments</p>
                  </div>
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-800">{healingMetrics.daysBetween}</p>
                    <p className="text-sm text-gray-500">Days Tracked</p>
                  </div>
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-800">
                      {sortedImages.length > 0 ? Math.ceil(healingMetrics.daysBetween / sortedImages.length) : 0}
                    </p>
                    <p className="text-sm text-gray-500">Avg Days Between</p>
                  </div>
                </div>

                {/* Trend Indicator */}
                <div className={`rounded-lg p-4 text-center ${
                  healingMetrics.isHealing === null ? 'bg-gray-50' :
                  healingMetrics.isHealing ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
                }`}>
                  {healingMetrics.isHealing === null ? (
                    <p className="text-gray-600">Add wound measurements to track healing trends</p>
                  ) : healingMetrics.isHealing ? (
                    <>
                      <TrendingDown className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <p className="text-lg font-semibold text-green-800">Wound is Healing</p>
                      <p className="text-sm text-green-600">Size has decreased by {healingMetrics.areaChange}%</p>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                      <p className="text-lg font-semibold text-orange-800">Needs Attention</p>
                      <p className="text-sm text-orange-600">Size has increased - review treatment plan</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>At least 2 images needed for metrics</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full Screen Comparison Modal */}
      {showComparison && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowComparison(false)}
        >
          <button
            onClick={() => setShowComparison(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-6xl w-full grid grid-cols-2 gap-8" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <p className="text-white text-lg font-medium mb-2">Initial</p>
              <img 
                src={compareImages.before?.imageData} 
                alt="Before"
                className="max-h-[70vh] mx-auto rounded-lg object-contain"
              />
              <p className="text-white text-sm mt-2">
                {compareImages.before && new Date(compareImages.before.dateTaken).toLocaleDateString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-white text-lg font-medium mb-2">Current</p>
              <img 
                src={compareImages.after?.imageData} 
                alt="After"
                className="max-h-[70vh] mx-auto rounded-lg object-contain"
              />
              <p className="text-white text-sm mt-2">
                {compareImages.after && new Date(compareImages.after.dateTaken).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WoundHealingTracker;
